import { Injectable } from '@angular/core';

import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest, HttpResponse } from '@angular/common/http';
import { Constants } from '../../constants';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { NotifService } from '../../core/notification/notif.service';

/**
 * Set on a request whose caller shows its own message for a failure, so the generic toast below
 * doesn't appear on top of it. Session handling for 401s still applies.
 */
export const SUPPRESS_ERROR_NOTIFICATION = new HttpContextToken<boolean>(() => false);

@Injectable({
    providedIn: 'root'
})
export class HttpErrorInterceptorService {

    constructor(
        private readonly notifService: NotifService,
        private readonly authService: AuthService
    ) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            tap((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.error && event.status !== 200) {
                        this.notifService.error(JSON.stringify(event.body.error));
                        throw (event.body.error);
                    }
                }
            }),
            catchError((err: any) => {
                if (err instanceof HttpErrorResponse) {
                    console.log(err);
                    if (err.status === Constants.AUTH_ERROR_CODE) {
                        if (this.isAerisApi(request.url)) {
                            console.log('Aeris Authentication...');
                            return throwError(err);
                        } else if (
                            err.error === "TOKEN_EXPIRED" ||
                            err.error === "ACCESS_TOKEN_EXPIRED" ||
                            err.error?.message === "ACCESS_TOKEN_EXPIRED"
                        ) {
                            return this.authService.refreshTokenAndRetry(request, next);
                        } else if (
                            err.error?.message === "INVALID_REFRESH_TOKEN" ||
                            err.error?.error === "INVALID_TOKEN" ||
                            err.error?.message === "Session expired or logged in elsewhere" ||
                            (typeof err.error === 'string' && err.error.includes('Session expired or logged in elsewhere'))
                        ) {
                            this.notifService.error("Session expired or logged in elsewhere");
                            this.authService.logout(false);
                            return throwError(err);
                        }
                    } else if (request.context.get(SUPPRESS_ERROR_NOTIFICATION)) {
                        // The caller reports this failure itself
                    } else if (err.status === Constants.FORBIDDEN_ERROR_CODE) {
                        const message = err.error?.message || 'You do not have permission to perform this action';
                        this.notifService.error(message);
                    } else if (err.status === Constants.TOO_MANY_REQUESTS) {
                        this.notifService.error('Too many requests, please try again after sometime');
                    } else if (err.status !== Constants.BUSINESS_ERROR_CODE && (!err.error || !err.error.errorId)) {
                        // this.notifService.error('mmm... something went wrong. Please come back later.');
                    } else if (err.status === Constants.BUSINESS_ERROR_CODE || err.error?.errorId) {
                        const message = err.error?.message || 'An error occurred';
                        const isGroupRequest = request.url.includes('/groups');
                        
                        // Check for duplicate group name errors
                        if (isGroupRequest && (
                            message.includes('already exists') || 
                            message.includes('duplicate') ||
                            message.includes('unique constraint') ||
                            (message.toLowerCase().includes('group') && message.toLowerCase().includes('exist')) ||
                            err.status === 503 || err.status === 500 || err.status === 409)) {
                            this.notifService.error('Group name already exists. Please choose a different name.');
                        } else {
                            this.notifService.error(message);
                        }
                    }
                }
                return throwError(err);
            })
        );
    }

    private isAerisApi(url: string): boolean {
        const aerieApiDomain = "aerisapi.com";
        const urlObject = new URL(url);
        return urlObject.hostname.includes(aerieApiDomain);
    }
}
